import type { AxiosResponse, RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { message, notification } from 'antd';
import configData from '../config/defaultSettings';
import {
  permissionInterceptor,
  permissionResponseInterceptor,
} from './middleware/apiPermissionGuard';

// 扩展 Axios 请求配置类型
interface ExtendedRequestOptions extends RequestOptions {
  metadata?: {
    startTime: number;
    requestId: string;
  };
}

// 与后端约定的响应数据格式
type ResponseStructure = AxiosResponse & { data: DEFAULT_CONFIG.DefaultResult };

// 特殊 HTTP 状态码枚举
const SPECIAL_HTTP_CODES = {
  UNAUTHORIZED: 401, // 未授权
  FORBIDDEN: 403, // 禁止访问
  NOT_FOUND: 404, // 资源不存在
  INTERNAL_SERVER_ERROR: 500, // 服务器内部错误
  SERVICE_UNAVAILABLE: 503, // 服务不可用
};

// 防重复提示的时间间隔 (毫秒)
const MESSAGE_DEBOUNCE_TIME = 3000;

// 记录最近一次提示的时间
let lastUnauthorizedMessageTime = 0;

// 用户友好的错误消息映射
const USER_FRIENDLY_MESSAGES = {
  [SPECIAL_HTTP_CODES.UNAUTHORIZED]: '登录已过期，请重新登录',
  [SPECIAL_HTTP_CODES.FORBIDDEN]: '您没有权限执行此操作',
  [SPECIAL_HTTP_CODES.NOT_FOUND]: '请求的资源不存在',
  [SPECIAL_HTTP_CODES.INTERNAL_SERVER_ERROR]: '服务器内部错误，请稍后重试',
  [SPECIAL_HTTP_CODES.SERVICE_UNAVAILABLE]: '服务暂时不可用，请稍后重试',
  NETWORK_ERROR: '网络连接异常，请检查网络设置',
  TIMEOUT_ERROR: '请求超时，请稍后重试',
  DEFAULT_ERROR: '操作失败，请稍后重试',
};

// 开发环境详细错误信息映射
const DEVELOPMENT_ERROR_DETAILS = {
  [SPECIAL_HTTP_CODES.UNAUTHORIZED]: '认证失败，可能token已过期或无效',
  [SPECIAL_HTTP_CODES.FORBIDDEN]: '权限不足，当前用户无权访问该资源',
  [SPECIAL_HTTP_CODES.NOT_FOUND]: 'API端点不存在或资源已被删除',
  [SPECIAL_HTTP_CODES.INTERNAL_SERVER_ERROR]:
    '服务器发生未知错误，建议联系管理员',
  [SPECIAL_HTTP_CODES.SERVICE_UNAVAILABLE]: '服务正在维护或负载过高',
};

/**
 * 清除用户认证信息
 */
const clearAuthInfo = () => {
  // 清除可能存在的认证信息
  // 注意：根据项目实际情况调整清除逻辑
  // 清除用户认证信息
};

/**
 * 处理401未授权错误
 */
const handleUnauthorizedError = (_error: any) => {
  // 记录错误日志
  // 检测到401未授权错误
  // 防重复提示机制
  const currentTime = Date.now();
  if (currentTime - lastUnauthorizedMessageTime < MESSAGE_DEBOUNCE_TIME) {
    // 跳过重复401错误提示
    // 仍然执行跳转逻辑，但不显示重复消息
    clearAuthInfo();
    const { pathname, search } = window.location;
    const redirectUrl = pathname + search;
    if (pathname !== '/login') {
      history.replace({
        pathname: '/login',
        search: redirectUrl
          ? `?redirect=${encodeURIComponent(redirectUrl)}`
          : '',
      });
    }
    return;
  }

  // 更新最后提示时间
  lastUnauthorizedMessageTime = currentTime;

  // 清除认证信息
  clearAuthInfo();

  // 显示用户友好的提示
  message.error(USER_FRIENDLY_MESSAGES[SPECIAL_HTTP_CODES.UNAUTHORIZED]);

  // 跳转到登录页
  const { pathname, search } = window.location;
  const redirectUrl = pathname + search;

  // 避免在登录页面重复跳转
  if (pathname !== '/login') {
    history.replace({
      pathname: '/login',
      search: redirectUrl ? `?redirect=${encodeURIComponent(redirectUrl)}` : '',
    });
  }
};

/**
 * 处理 500 服务器内部错误
 */
const handleInternalServerError = (_error: any) => {
  // 服务器内部错误处理
  // 显示用户友好的提示
  message.error(
    USER_FRIENDLY_MESSAGES[SPECIAL_HTTP_CODES.INTERNAL_SERVER_ERROR],
  );

  // 开发环境下显示更多调试信息
  if (process.env.NODE_ENV === 'development') {
    notification.error({
      message: '服务器错误详情',
      description:
        DEVELOPMENT_ERROR_DETAILS[SPECIAL_HTTP_CODES.INTERNAL_SERVER_ERROR],
      duration: 10,
    });
  }
};

/**
 * 检查是否为数据库无结果错误
 * @param errorMessage 错误消息
 * @returns 是否为数据库无结果错误
 */
const isDatabaseNoRowsError = (errorMessage: string): boolean => {
  if (!errorMessage) return false;
  const lowerCaseMessage = errorMessage.toLowerCase();
  return lowerCaseMessage.includes('no rows in result set');
};

/**
 * 处理通用HTTP错误
 */
const handleHttpError = (error: any) => {
  const statusCode = error.response?.status;

  // 根据不同的状态码进行特殊处理
  switch (statusCode) {
    case SPECIAL_HTTP_CODES.UNAUTHORIZED:
      handleUnauthorizedError(error);
      break;
    case SPECIAL_HTTP_CODES.INTERNAL_SERVER_ERROR:
      handleInternalServerError(error);
      break;
    case SPECIAL_HTTP_CODES.FORBIDDEN:
      message.error(USER_FRIENDLY_MESSAGES[SPECIAL_HTTP_CODES.FORBIDDEN]);
      break;
    case SPECIAL_HTTP_CODES.NOT_FOUND:
      message.error(USER_FRIENDLY_MESSAGES[SPECIAL_HTTP_CODES.NOT_FOUND]);
      break;
    case SPECIAL_HTTP_CODES.SERVICE_UNAVAILABLE:
      message.error(
        USER_FRIENDLY_MESSAGES[SPECIAL_HTTP_CODES.SERVICE_UNAVAILABLE],
      );
      break;
    default: {
      // 其他HTTP错误使用默认处理
      const defaultMessage = `请求失败 (${statusCode})`;
      message.error(error.response?.data?.message || defaultMessage);
      break;
    }
  }
};

/**
 * 处理网络错误
 */
const handleNetworkError = (error: any) => {
  // 网络错误处理

  // 判断是否为超时错误
  if (error?.code === 'ECONNABORTED') {
    message.error(USER_FRIENDLY_MESSAGES.TIMEOUT_ERROR);
  } else {
    message.error(USER_FRIENDLY_MESSAGES.NETWORK_ERROR);
  }

  // 开发环境下提供更多调试信息
  if (process.env.NODE_ENV === 'development') {
    notification.warning({
      message: '网络连接问题',
      description: '请检查网络连接状态和API服务是否正常运行',
      duration: 8,
    });
  }
};

/**
 * 获取用户友好的错误消息
 */
const getUserFriendlyMessage = (error: any): string => {
  // 优先处理业务错误，显示后端返回的具体错误信息
  if (error.name === 'BizError' && error.info) {
    return (
      error.info.errorMessage ||
      error.info.message ||
      USER_FRIENDLY_MESSAGES.DEFAULT_ERROR
    );
  }

  // 处理HTTP响应错误
  if (error.response) {
    const statusCode = error.response.status;
    // 如果响应中有具体的错误信息，优先显示
    if (error.response.data && typeof error.response.data === 'object') {
      const responseData = error.response.data;
      // 尝试从响应数据中获取错误消息
      if (responseData.message) {
        return responseData.message;
      }
      if (responseData.errorMessage) {
        return responseData.errorMessage;
      }
    }
    // 使用预定义的友好消息
    return (
      USER_FRIENDLY_MESSAGES[
        statusCode as keyof typeof USER_FRIENDLY_MESSAGES
      ] || `请求失败 (${statusCode})`
    );
  }

  // 处理网络错误
  if (error.request) {
    return USER_FRIENDLY_MESSAGES.NETWORK_ERROR;
  }

  // 默认错误消息
  return USER_FRIENDLY_MESSAGES.DEFAULT_ERROR;
};

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改写
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理：umi@3 的错误处理方案
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { data } = res as unknown as ResponseStructure;
      const { code, message } = data;
      if (code !== configData.requestSuccessCode) {
        const error: any = new Error(message);
        error.name = 'BizError';
        error.info = {
          errorCode: code,
          errorMessage: message,
          data: data.data || null,
        };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;

      // 我们捕获 errorThrower 抛出的错误
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          // 记录业务错误
          // BizError 处理

          // 🔒 特殊处理：数据库无结果错误（no rows in result set）统一提示重新登录
          const errorMessage =
            (errorInfo as any).errorMessage ||
            (errorInfo as any).message ||
            getUserFriendlyMessage(error);

          if (isDatabaseNoRowsError(errorMessage)) {
            // 数据库查询无结果，清除认证信息并跳转到登录页
            // handleUnauthorizedError 内部会显示 "登录已过期，请重新登录" 提示
            handleUnauthorizedError(error);
            return;
          }

          // 显示用户友好的错误消息
          // - 优先显示后端返回的具体错误信息
          message.error(errorMessage);
        }
      } else if (error.response) {
        // HTTP响应错误（状态码超出2xx范围）
        // HTTP响应错误处理

        // 处理特定的HTTP状态码
        handleHttpError(error);
      } else if (error.request) {
        // 网络错误（请求发出但无响应）
        handleNetworkError(error);
      } else {
        // 其他请求错误
        // 请求错误处理

        message.error(getUserFriendlyMessage(error));
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    // 1. 权限检查拦截器 (异步)
    async (config: RequestOptions) => {
      // 执行权限检查拦截器
      const enhancedConfig = await permissionInterceptor(
        config.url || '',
        config as any,
      );
      return enhancedConfig;
    },

    // 2. 路径标准化和元数据注入拦截器 (同步)
    (config: RequestOptions) => {
      // 2.1 提取当前页面路径 (用于审计)
      let currentPath = '';
      try {
        const hash = window.location.hash || '';
        // 处理 hash 路由，例如：#/event/order?query=123 -> /event/order
        if (hash && hash.length > 1) {
          // 去掉 # 符号
          const hashWithoutHash = hash.substring(1);
          // 去掉查询参数
          const [pathname] = hashWithoutHash.split('?');
          // 确保路径以 / 开头
          currentPath = pathname?.startsWith('/')
            ? pathname
            : `/${pathname || ''}`;
        }
      } catch (_e) {
        // 提取失败时保持空字符串
      }

      // 2.2 标准化API路径
      const fullPath = config.url || '';
      let path = fullPath;

      if (fullPath.startsWith('http')) {
        // 完整URL，提取路径部分
        try {
          const urlObj = new URL(fullPath);
          path = urlObj.pathname + urlObj.search;
        } catch (_e) {
          // 解析失败保持原样
        }
      }
      // 相对路径直接使用
      else if (fullPath.startsWith('/api')) {
        path = fullPath;
      } else {
        path = fullPath;
      }

      // 2.3 添加请求元数据和自定义header
      const enhancedConfig: ExtendedRequestOptions = {
        ...config,
        headers: {
          ...config.headers,
          path: currentPath, // 当前页面路径 (用于审计)
          'X-API-Path': path, // API路径 (用于调试)
        },
        metadata: {
          startTime: Date.now(),
          requestId: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        },
      };

      // 2.4 开发环境下记录请求日志
      return enhancedConfig;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    // 1. 权限相关响应拦截器 (异步)
    async (response) => {
      const processedResponse = await permissionResponseInterceptor(
        response as any,
        response.config as any,
      );

      return processedResponse;
    },

    // 2. 数据格式化和错误检查拦截器 (同步)
    (response) => {
      // 2.1 解析响应数据
      const res = response as unknown as ResponseStructure;

      // 2.2 判断响应格式类型
      // 格式 A: { code: 0, message: 'success', data: {...} } (标准格式，data 在根级别)
      // 格式 B: { data: { code: 0, message: 'success', data: {...} } } (嵌套格式，经过 axios 包装)
      const hasCodeInRoot = 'code' in res && (res as any).code !== undefined;
      const hasCodeInData =
        res.data && typeof res.data === 'object' && 'code' in res.data;

      let code: number;
      let responseMessage: string;
      let data: any;

      if (hasCodeInRoot) {
        // 格式 A：标准格式（code 在根级别）
        code = (res as any).code;
        responseMessage = (res as any).message || '';
        data = (res as any).data;
      } else if (hasCodeInData) {
        // 格式 B：嵌套格式（code 在 data 里面，axios 包装）
        const responseData = res.data;
        code = responseData.code;
        responseMessage = responseData.message || '';
        data = responseData.data;
      } else {
        // 未知格式，使用默认
        code = 0;
        responseMessage = '';
        data = res?.data || {};
      }

      // 2.3 记录响应性能 (开发环境下)
      // const extendedConfig = response.config as ExtendedRequestOptions;

      // 2.4 特殊情况处理：code=61 或消息包涵"异常登录"的处理
      // code 为 61：只显示错误消息，不触发业务错误处理，避免跳转到登录页
      // if (code === 61) {
      //   // 显示后端返回的错误消息
      //   const errorMessage = responseMessage || data?.message || '请求失败';
      //   message.error(errorMessage);

      //   // 直接返回成功，不触发后续的 bizError 处理流程
      //   // 将 code 改为成功码，避免触发业务错误处理
      //   code = configData.requestSuccessCode;
      //   // 设置 response.data 为 null 或空对象，避免页面获取到错误数据
      //   (response as any).data = data || null;
      //   return response as any;
      // }

      // 消息包含"异常登录"：按 401 未授权错误处理
      if (responseMessage?.includes('异常登录')) {
        const unauthorizedError: any = new Error('未授权访问');
        unauthorizedError.response = {
          status: SPECIAL_HTTP_CODES.UNAUTHORIZED,
          statusText: 'Unauthorized',
          data: data || {},
        };
        unauthorizedError.config = response.config;
        throw unauthorizedError;
      }

      // 2.5 业务错误处理：code !== 0 表示失败
      if (code !== configData.requestSuccessCode) {
        const bizError = new Error(responseMessage);
        (bizError as any).name = 'BizError';
        (bizError as any).info = {
          errorCode: code,
          errorMessage: responseMessage,
          data: data || null,
          response: res,
          success: false,
        };
        throw bizError;
      }

      // 2.6 成功：返回 data 字段
      // 关键修复：直接修改 response.data，而不是返回新对象
      // umi-request 会自动使用修改后的 response.data

      const result = data;

      // 直接修改原始 response 对象的 data 属性
      (response as any).data = result;

      // 返回修改后的 response 对象（保持类型一致）
      return response as any;
    },
  ],
};
